<template>
  <div>
    <div
      v-for="{ typ, dokumente, maxAnzahl, uploadMoeglich } in dokumentSlots"
      :key="typ.key"
      class="teilnehmer-dokumente__slot"
    >
      <div class="d-flex align-items-center mb-1">
        <span class="teilnehmer-dokumente__label">{{ typ.label }}</span>
        <!-- Erklärt bei Typen mit Hinweistext, wofür die Kategorie gedacht ist -->
        <span
          v-if="typ.hinweis"
          class="teilnehmer-dokumente__info ml-2"
          :title="typ.hinweis"
          v-b-tooltip.hover="{ customClass: 'teilnehmer-dokumente-tooltip' }"
        >
          <Info :size="14" />
        </span>
        <!-- Optionale Typen bekommen keine Statusanzeige -->
        <span v-if="typ.optional !== true" class="ml-auto d-inline-flex align-items-center">
          <Check v-if="dokumente.length > 0" :size="16" class="text-success" />
          <CircleAlert v-else :size="16" class="text-danger" />
        </span>
      </div>

      <div v-for="dokument in dokumente" :key="dokument.id" class="teilnehmer-dokumente__item">
        <div class="d-flex align-items-center" style="min-width: 0">
          <component
            :is="getFileIcon(dokument.dateiname)"
            :size="16"
            class="mr-2 flex-shrink-0"
            style="color: #3699ff"
          />
          <span
            class="teilnehmer-dokumente__name"
            :class="{ 'teilnehmer-dokumente__name--downloadable': downloadAllowed }"
            @click="downloadAllowed && downloadDokument(dokument)"
          >
            {{ dokument.dateiname }}
          </span>
          <span class="teilnehmer-dokumente__size ml-2">({{ formatFileSize(dokument.groesse) }})</span>
        </div>
        <div class="d-flex align-items-center" style="gap: 8px">
          <span
            v-if="downloadAllowed"
            class="teilnehmer-dokumente__action"
            @click="downloadDokument(dokument)"
          >
            <Download :size="16" />
          </span>
          <span
            v-if="!readonly"
            class="teilnehmer-dokumente__action teilnehmer-dokumente__action--delete"
            @click="deleteDokument(dokument)"
          >
            <Trash2 :size="16" />
          </span>
        </div>
      </div>

      <!-- Das Upload-Feld steht nur, solange der Typ noch freie Plätze hat -->
      <DateiUpload
        v-if="uploadMoeglich"
        :value="[]"
        :allowMultipleFiles="maxAnzahl > 1"
        :acceptFormats="acceptFormats"
        @input="files => uploadDokument(typ, files, dokumente.length)"
      />
      <div v-else-if="!readonly && maxAnzahl > 1" class="text-muted font-size-sm">
        Maximal {{ maxAnzahl }} "Sonstige" Dokumente
      </div>
      <div v-else-if="dokumente.length === 0" class="text-muted font-size-sm">Kein Dokument vorhanden</div>
    </div>

    <!-- LÖSCHEN MODAL -->
    <b-modal
      v-model="showDeleteModal"
      centered
      hide-header
      hide-footer
      content-class="teilnehmer-dokumente-confirm"
      body-class="teilnehmer-dokumente-confirm__body"
      @hidden="dokumentZumLoeschen = null"
    >
      <h5 class="teilnehmer-dokumente-confirm__title">Dokument löschen?</h5>
      <p class="teilnehmer-dokumente-confirm__text">
        Soll das Dokument
        <strong>"{{ dokumentZumLoeschen && dokumentZumLoeschen.dateiname }}"</strong>
        wirklich gelöscht werden?
      </p>
      <p class="teilnehmer-dokumente-confirm__text mb-0">
        Diese Aktion kann nicht rückgängig gemacht werden.
      </p>
      <div class="teilnehmer-dokumente-confirm__footer">
        <b-button
          variant="outline-secondary"
          class="teilnehmer-dokumente-confirm__btn"
          @click="showDeleteModal = false"
        >
          Abbrechen
        </b-button>
        <b-button variant="danger" class="teilnehmer-dokumente-confirm__btn" @click="confirmDelete">
          Löschen
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import DateiUpload from '@/components/common/datei-upload.vue';
import { formatFileSize, getFileIcon } from '@/core/common/helpers/datei.utils';
import { getSichtbareDokumentTypen } from '@/core/vorgaenge/helpers/teilnehmer-passdaten.utils';
import {
  uploadTeilnehmerDokument,
  downloadTeilnehmerDokument,
  deleteTeilnehmerDokument,
} from '@/core/vorgaenge/teilnehmer-passdaten.service';
import {
  FileText,
  Image as ImageIcon,
  FileSpreadsheet,
  File,
  Download,
  Trash2,
  Check,
  CircleAlert,
  Info,
} from 'lucide-vue';

export default {
  name: 'TeilnehmerDokumente',
  components: {
    FileText,
    ImageIcon,
    FileSpreadsheet,
    File,
    Download,
    Trash2,
    Check,
    CircleAlert,
    Info,
    DateiUpload,
  },
  props: {
    vorgangId: { type: Number, required: true },
    teilnehmer: { type: Object, required: true },
    konfiguration: { type: Object },
    dokumente: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    downloadAllowed: { type: Boolean, default: true },
  },
  data() {
    return {
      acceptFormats: ['.pdf', '.jpg', '.jpeg', '.png'],
      showDeleteModal: false,
      dokumentZumLoeschen: null,
    };
  },
  computed: {
    sichtbareTypen() {
      return getSichtbareDokumentTypen(this.konfiguration);
    },
    // Gruppiert die Dokumente je Typ als Liste, da ein Typ mehrere Dokumente aufnehmen kann
    dokumenteByTyp() {
      return this.dokumente.reduce((acc, dokument) => {
        acc[dokument.dokumentTyp] = [...(acc[dokument.dokumentTyp] || []), dokument];
        return acc;
      }, {});
    },
    // Baut je sichtbarem Typ das Anzeigemodell aus vorhandenen Dokumenten und Obergrenze
    // Typen ohne maxAnzahl nehmen wie bisher genau ein Dokument auf
    dokumentSlots() {
      return this.sichtbareTypen.map(typ => {
        const dokumente = this.dokumenteByTyp[typ.key] || [];
        const maxAnzahl = typ.maxAnzahl || 1;
        return {
          typ,
          dokumente,
          maxAnzahl,
          uploadMoeglich: !this.readonly && dokumente.length < maxAnzahl,
        };
      });
    },
  },
  methods: {
    formatFileSize,
    getFileIcon,

    // Lädt die Dateien der Reihe nach hoch, begrenzt auf die noch freien Plätze des Typs
    async uploadDokument(typ, files, vorhandeneAnzahl) {
      if (!files || files.length === 0) return;
      const freiePlaetze = (typ.maxAnzahl || 1) - vorhandeneAnzahl;
      const zuLadendeDateien = files.slice(0, freiePlaetze);
      if (zuLadendeDateien.length === 0) return;
      try {
        let aktualisierterTeilnehmer = {};
        for (const datei of zuLadendeDateien) {
          aktualisierterTeilnehmer = await uploadTeilnehmerDokument(
            this.vorgangId,
            this.teilnehmer.nummer,
            typ.key,
            datei
          );
        }
        this.toast(this.$t('WORKFLOWS.COMMON.SAVED'));
        this.$emit('uploaded', {
          vorgangId: this.vorgangId,
          teilnehmerNummer: this.teilnehmer.nummer,
          dokumente: aktualisierterTeilnehmer.dokumente || [],
        });
      } catch (error) {
        console.error('Fehler beim Hochladen des Dokuments:', error);
        this.toast('Fehler beim Hochladen des Dokuments', { variant: 'danger' });
      }
    },
    async downloadDokument(dokument) {
      try {
        await downloadTeilnehmerDokument(dokument);
      } catch (error) {
        console.error('Fehler beim Herunterladen des Dokuments:', error);
        this.toast('Fehler beim Herunterladen des Dokuments', { variant: 'danger' });
      }
    },
    // Das Löschen adressiert das konkrete Dokument, da ein Typ mehrere Dokumente enthalten kann
    deleteDokument(dokument) {
      this.dokumentZumLoeschen = dokument;
      this.showDeleteModal = true;
    },
    async confirmDelete() {
      const dokument = this.dokumentZumLoeschen;
      this.showDeleteModal = false;
      if (!dokument) return;
      try {
        await deleteTeilnehmerDokument(dokument);
        this.$emit('deleted', dokument);
      } catch (error) {
        console.error('Fehler beim Löschen des Dokuments:', error);
        this.toast('Fehler beim Löschen des Dokuments', { variant: 'danger' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.teilnehmer-dokumente__slot {
  margin-bottom: 12px;
}
.teilnehmer-dokumente__label {
  color: #364153d9;
  font-family: Poppins, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 0;
}
.teilnehmer-dokumente__info {
  display: inline-flex;
  flex-shrink: 0;
  color: var(--text-muted);
  cursor: default;
}
.teilnehmer-dokumente__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}
.teilnehmer-dokumente__item:hover {
  border-color: #3699ff;
}
// Abstand zwischen mehreren Dokumenten eines Typs und zum folgenden Upload-Feld
.teilnehmer-dokumente__item + * {
  margin-top: 4px;
}
.teilnehmer-dokumente__name {
  font-size: 13px;
  color: #364153;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.teilnehmer-dokumente__name--downloadable {
  cursor: pointer;
}
.teilnehmer-dokumente__size {
  font-size: 10px;
  color: #9ca3af;
  flex-shrink: 0;
}
.teilnehmer-dokumente__action {
  flex-shrink: 0;
  color: #6b7280;
  cursor: pointer;
}
.teilnehmer-dokumente__action:hover,
.teilnehmer-dokumente__name--downloadable:hover {
  color: #3699ff;
}
.teilnehmer-dokumente__action--delete:hover {
  color: #f64e60;
}
</style>

<style lang="scss">
// Der Hinweistext
.teilnehmer-dokumente-tooltip .tooltip-inner {
  max-width: 230px;
  text-align: left;
}
.teilnehmer-dokumente-confirm.modal-content {
  border: none;
  border-radius: 24px;
  box-shadow: 0px 25px 50px -12px #00000040;
}
.teilnehmer-dokumente-confirm__body {
  padding: 28px 32px;
}
.teilnehmer-dokumente-confirm__title {
  margin-bottom: 16px;
  font-weight: 700;
  font-size: 1.25rem;
  color: #101828;
}
.teilnehmer-dokumente-confirm__text {
  margin-bottom: 12px;
  color: #364153;
  font-size: 0.95rem;

  strong {
    color: #101828;
    font-weight: 700;
  }
}
.teilnehmer-dokumente-confirm__footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
}
.teilnehmer-dokumente-confirm__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 32px;
  border-radius: 8px;
  font-weight: 600;

  &.btn-danger {
    background-color: #d4183d;
    border-color: #d4183d;

    &:hover,
    &:focus,
    &:active,
    &:not(:disabled):not(.disabled):active {
      background-color: #b3122f;
      border-color: #b3122f;
    }
  }
}
</style>
